import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let id = localStorage.getItem("ybera_chat_session");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("ybera_chat_session", id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm Ybera's AI stylist 💛 Want me to build your ideal hair routine? Ask me anything or tap a question below." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  // Proactively open the chat once per session to invite the visitor.
  useEffect(() => {
    if (sessionStorage.getItem("ybera_chat_autoopened")) return;
    const t = setTimeout(() => {
      setOpen(true);
      sessionStorage.setItem("ybera_chat_autoopened", "1");
    }, 8000);
    return () => clearTimeout(t);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId.current,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Sorry, I had trouble replying. Please try again in a moment." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = [
    "How do I control frizz?",
    "Which kit for damaged hair?",
    "How does the affiliate program work?",
  ];

  return (
    <>
      <button
        aria-label="Open chat"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#1a1a1a] text-[#C9A961] flex items-center justify-center shadow-2xl hover:bg-[#C9A961] hover:text-[#1a1a1a] transition-colors"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-[380px] h-[560px] max-h-[80vh] bg-white shadow-2xl border border-[#EAE3D8] flex flex-col animate-in fade-in slide-in-from-bottom-4">
          <div className="bg-[#1a1a1a] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#C9A961]/20 flex items-center justify-center text-[#C9A961]">
                <Sparkles size={16} />
              </div>
              <div>
                <p className="font-serif-y text-[17px] leading-none">Ybera AI Stylist</p>
                <p className="font-sans-y text-[10px] tracking-[0.15em] uppercase text-[#C9A961] mt-1">
                  Powered by Claude
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" className="text-white/70 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FBF8F4]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 font-sans-y text-[13.5px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#1a1a1a] text-white"
                      : "bg-white border border-[#EAE3D8] text-[#1a1a1a]"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#EAE3D8] px-4 py-2.5 flex items-center gap-2 text-[#7a7a7a]">
                  <Loader2 size={14} className="animate-spin" />
                  <span className="font-sans-y text-[13px]">Ybera is typing...</span>
                </div>
              </div>
            )}
          </div>

          {messages.length <= 1 && !loading && (
            <div className="px-3 pb-2 flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => { setInput(s); setTimeout(send, 50); }}
                  className="font-sans-y text-[11px] px-3 py-1.5 border border-[#EAE3D8] text-[#333] hover:border-[#C9A961] hover:text-[#C9A961] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="p-3 border-t border-[#EAE3D8] bg-white flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about your hair or Ybera..."
              className="flex-1 bg-[#FBF8F4] border border-[#EAE3D8] px-3 py-2.5 font-sans-y text-[13.5px] focus:outline-none focus:border-[#C9A961]"
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              aria-label="Send"
              className="w-10 h-10 bg-[#1a1a1a] text-[#C9A961] hover:bg-[#C9A961] hover:text-[#1a1a1a] disabled:opacity-50 flex items-center justify-center transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
